/**
 * Retirement Planning Calculator Page
 * Plan your retirement savings and estimate your retirement income
 */
import { useState, useEffect } from 'react';
import { useCustomTranslation } from '../../hooks';
import { trackPageView } from '../../utils/analytics';
import { Box, Typography, TextField, Button, Card, CardContent } from '@mui/material';
import AmazonRecommendation from '../../components/common/AmazonRecommendation';


interface RetirementPlanningData {
  current_age: number;
  retirement_age: number;
  current_savings: number;
  monthly_contribution: number;
  annual_return_rate: number;
  monthly_retirement_income: number;
}

interface RetirementPlanningResults {
  retirement_savings: number;
  years_to_save: number;
  total_contributed: number;
  interest_earned: number;
  retirement_fund_duration: number;
  shortfall_or_surplus: number;
}

export default function RetirementPlanningPage() {
  const { t, i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';
  const [formData, setFormData] = useState<RetirementPlanningData>({
    current_age: 30,
    retirement_age: 65,
    current_savings: 0,
    monthly_contribution: 0,
    annual_return_rate: 7,
    monthly_retirement_income: 0,
  });
  const [results, setResults] = useState<RetirementPlanningResults | null>(null);

  useEffect(() => {
    trackPageView('/calculators/retirement-planning', 'Retirement Planning Calculator');
    document.title = `${t('calculators.retirementPlanning.title')} | PayWise365`;
  }, [t]);

  const handleCalculate = () => {
    const years_to_save = formData.retirement_age - formData.current_age;
    const monthlyRate = formData.annual_return_rate / 100 / 12;
    const totalMonths = years_to_save * 12;
    let balance = formData.current_savings;

    // Calculate retirement savings
    for (let i = 0; i < totalMonths; i++) {
      balance = balance * (1 + monthlyRate) + formData.monthly_contribution;
    }

    const total_contributed = formData.current_savings + (formData.monthly_contribution * totalMonths);
    const interest_earned = balance - total_contributed;
    
    // Calculate how long retirement fund will last
    const annual_retirement_need = formData.monthly_retirement_income * 12;
    const retirement_fund_duration = balance / annual_retirement_need;
    const shortfall_or_surplus = balance - (annual_retirement_need * 20); // Assuming 20 years retirement

    setResults({
      retirement_savings: balance,
      years_to_save,
      total_contributed,
      interest_earned,
      retirement_fund_duration,
      shortfall_or_surplus,
    });
  };

  return (
    <div className="calculator-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>{t('calculators.retirementPlanning.title')}</h1>
          <p>{t('calculators.retirementPlanning.pageSubtitle')}</p>
        </div>
      </div>
      
      <main className="calculator-content">
        <div className="calculator-container">
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {t('form.title')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <TextField
                    fullWidth
                    label="Current Age"
                    type="number"
                    value={formData.current_age}
                    onChange={(e) => setFormData({ ...formData, current_age: parseInt(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Retirement Age"
                    type="number"
                    value={formData.retirement_age}
                    onChange={(e) => setFormData({ ...formData, retirement_age: parseInt(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Current Savings (HKD)"
                    type="number"
                    value={formData.current_savings || ''}
                    onChange={(e) => setFormData({ ...formData, current_savings: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Monthly Contribution (HKD)"
                    type="number"
                    value={formData.monthly_contribution || ''}
                    onChange={(e) => setFormData({ ...formData, monthly_contribution: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Annual Return Rate (%)"
                    type="number"
                    value={formData.annual_return_rate}
                    onChange={(e) => setFormData({ ...formData, annual_return_rate: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Desired Monthly Retirement Income (HKD)"
                    type="number"
                    value={formData.monthly_retirement_income || ''}
                    onChange={(e) => setFormData({ ...formData, monthly_retirement_income: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <Button variant="contained" fullWidth onClick={handleCalculate} size="large">
                    Calculate
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {results && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3 }}>
                  Retirement Projection
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'success.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Retirement Savings at {formData.retirement_age}</Typography>
                      <Typography variant="h5">HKD {results.retirement_savings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'info.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Years to Save</Typography>
                      <Typography variant="h5">{results.years_to_save} years</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Total Contributed</Typography>
                      <Typography variant="h5">HKD {results.total_contributed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'warning.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Interest/Gains Earned</Typography>
                      <Typography variant="h5">HKD {results.interest_earned.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: results.shortfall_or_surplus > 0 ? 'success.dark' : 'error.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">
                        {results.shortfall_or_surplus > 0 ? 'Surplus' : 'Shortfall'} (vs 20-year retirement need)
                      </Typography>
                      <Typography variant="h5">
                        HKD {Math.abs(results.shortfall_or_surplus).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
      <div style={{ width: '100%', padding: '30px 20px' }}>
        <AmazonRecommendation isEnglish={isEnglish} />
      </div>
    </div>
  );
}
